// components/qualificacao/PainelQualificar.js — o painel de marcar o lead
// (out/26, rastreamento PR 2).
//
// Abre por cima de quem chamou (janela do contato, barra de lote, topo do
// chat) e tem três passos: escolher a marcação; o motivo, se desqualificou;
// o valor, se fechou venda. Venda não aparece no lote: o valor é de cada
// venda. Cores só por token (`--ch-*`): segue o tema claro/escuro do chat e
// do CRM sem mais nada.
//
// `onEscolher(pedido)` devolve uma Promise: resolvida, o painel fecha;
// rejeitada, a mensagem aparece aqui dentro e a pessoa corrige sem perder o
// que digitou.

import { useEffect, useRef, useState } from 'react';
import { Box, Popover } from '@mui/material';
import { FiArrowLeft, FiCheck, FiChevronRight } from 'react-icons/fi';
import { MOTIVOS, MOTIVO_TEXTO_MAX, ORDEM, STATUS, formatarValor, lerValor } from './regras';

const FONTE = "'Outfit', system-ui, sans-serif";
const foco = { '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 } };

const item = {
  ...foco, width: '100%', display: 'flex', alignItems: 'center', gap: '10px', p: '10px 12px', border: 0,
  borderRadius: '10px', bgcolor: 'transparent', color: 'var(--ch-text)', cursor: 'pointer', textAlign: 'left',
  fontFamily: FONTE, fontSize: 14.5, '&:hover': { bgcolor: 'var(--ch-surf)' }, '&:disabled': { opacity: 0.55, cursor: 'default' },
};

const campo = {
  width: '100%', boxSizing: 'border-box', height: 40, p: '0 12px', borderRadius: '10px', border: '1px solid var(--ch-bdr2)',
  bgcolor: 'var(--ch-surf)', color: 'var(--ch-text)', fontFamily: FONTE, fontSize: 14.5, outline: 0,
  '&:focus-visible': { borderColor: 'var(--ch-green)', boxShadow: '0 0 0 3px var(--ch-green-glow, rgba(0,214,124,.18))' },
};

function Botao({ children, onClick, principal, disabled, type = 'button' }) {
  return (
    <Box component="button" type={type} onClick={onClick} disabled={disabled} sx={{
      ...foco, height: 38, px: '14px', borderRadius: '10px', cursor: 'pointer', fontFamily: FONTE, fontSize: 14, fontWeight: 600,
      border: `1px solid ${principal ? 'var(--ch-green)' : 'var(--ch-bdr)'}`,
      bgcolor: principal ? 'var(--ch-green)' : 'transparent', color: principal ? '#fff' : 'var(--ch-text2)',
      '&:hover': { filter: principal ? 'brightness(1.06)' : 'none', color: principal ? '#fff' : 'var(--ch-text)' },
      '&:disabled': { opacity: 0.5, cursor: 'default', filter: 'none' },
    }}>
      {children}
    </Box>
  );
}

function Topo({ children, onVoltar }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', p: '12px 14px 6px' }}>
      {onVoltar && (
        <Box component="button" type="button" onClick={onVoltar} aria-label="Voltar" sx={{
          ...foco, width: 28, height: 28, p: 0, ml: '-6px', border: 0, borderRadius: '8px', bgcolor: 'transparent',
          color: 'var(--ch-text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          '&:hover': { bgcolor: 'var(--ch-surf)', color: 'var(--ch-text)' },
        }}>
          <FiArrowLeft size={15} />
        </Box>
      )}
      <Box component="span" sx={{ fontFamily: FONTE, fontSize: 15, fontWeight: 700 }}>{children}</Box>
    </Box>
  );
}

export default function PainelQualificar({
  anchorEl, onClose, atual = null, lote = false, titulo, passoInicial = 'inicio', acima = false, onEscolher,
}) {
  const aberto = Boolean(anchorEl);
  const [passo, setPasso] = useState(passoInicial);
  const [motivo, setMotivo] = useState('');
  const [texto, setTexto] = useState('');
  const [valor, setValor] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const primeiro = useRef(null);

  useEffect(() => {
    if (!aberto) return;
    setPasso(passoInicial); setMotivo(''); setTexto(''); setValor(''); setErro(''); setSalvando(false);
  }, [aberto, passoInicial]);

  // Foco no primeiro controle de cada passo (teclado e leitor de tela).
  useEffect(() => {
    if (!aberto) return undefined;
    const t = setTimeout(() => primeiro.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [aberto, passo]);

  const enviar = async (pedido) => {
    setSalvando(true);
    setErro('');
    try {
      await onEscolher(pedido);
      onClose();
    } catch (e) {
      setErro(e?.message || 'Não foi possível marcar. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  };

  const valorLido = lerValor(valor);
  const motivoOk = motivo && (motivo !== 'outro' || texto.trim());
  const voltar = passoInicial === 'inicio' ? () => { setPasso('inicio'); setErro(''); } : undefined;
  const opcoes = ORDEM.filter((k) => !(lote && k === 'venda'));

  return (
    <Popover
      open={aberto}
      anchorEl={anchorEl}
      onClose={salvando ? undefined : onClose}
      anchorOrigin={acima ? { vertical: 'top', horizontal: 'center' } : { vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={acima ? { vertical: 'bottom', horizontal: 'center' } : { vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          'data-screen-label': 'Qualificar lead',
          sx: {
            mt: acima ? '-8px' : '6px', width: 312, maxWidth: 'calc(100vw - 24px)', borderRadius: '14px', backgroundImage: 'none',
            bgcolor: 'var(--ch-card)', color: 'var(--ch-text)', border: '1px solid var(--ch-bdr)',
            boxShadow: '0 18px 44px rgba(15,28,50,.20)', fontFamily: FONTE,
          },
        },
      }}
    >
      {passo === 'inicio' && (
        <Box>
          <Topo>{titulo || 'Qualificar lead'}</Topo>
          <Box role="list" sx={{ p: '2px 6px 6px' }}>
            {opcoes.map((k, i) => {
              const t = STATUS[k];
              const marcado = atual === k;
              const comPasso = k !== 'qualificado';
              return (
                <Box role="listitem" key={k}>
                  <Box component="button" type="button" ref={i === 0 ? primeiro : undefined} disabled={salvando}
                    aria-pressed={marcado}
                    onClick={() => (comPasso ? setPasso(k) : enviar({ status: k }))}
                    sx={item}>
                    <Box component="span" aria-hidden sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: t.ponto, flexShrink: 0 }} />
                    <Box component="span" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <Box component="span" sx={{ fontWeight: 600 }}>{t.rotulo}</Box>
                      <Box component="span" sx={{ fontSize: 12.5, color: 'var(--ch-text3)' }}>{t.dica}</Box>
                    </Box>
                    {marcado && <FiCheck size={15} aria-label="marcação atual" style={{ color: t.ponto, flexShrink: 0 }} />}
                    {comPasso && <FiChevronRight size={15} aria-hidden style={{ color: 'var(--ch-text3)', flexShrink: 0 }} />}
                  </Box>
                </Box>
              );
            })}
            {(atual || lote) && (
              <>
                <Box aria-hidden sx={{ height: '1px', bgcolor: 'var(--ch-bdr)', m: '6px 8px' }} />
                <Box component="button" type="button" disabled={salvando} onClick={() => enviar({ status: null })}
                  sx={{ ...item, color: 'var(--ch-text2)', fontSize: 14 }}>
                  <Box component="span" aria-hidden sx={{ width: 9, height: 9, borderRadius: '50%', border: '1.5px dashed var(--ch-text3)', flexShrink: 0 }} />
                  Remover marcação
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {passo === 'desqualificado' && (
        <Box component="form" onSubmit={(e) => { e.preventDefault(); if (motivoOk) enviar({ status: 'desqualificado', motivo, motivo_texto: motivo === 'outro' ? texto.trim() : null }); }}>
          <Topo onVoltar={voltar}>Por que desqualificar?</Topo>
          <Box role="radiogroup" aria-label="Motivo" sx={{ p: '2px 6px 0' }}>
            {MOTIVOS.map(([k, r], i) => (
              <Box component="button" type="button" key={k} role="radio" aria-checked={motivo === k}
                ref={i === 0 ? primeiro : undefined} onClick={() => setMotivo(k)} disabled={salvando}
                sx={{ ...item, p: '8px 12px', fontSize: 14 }}>
                <Box component="span" aria-hidden sx={{
                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
                  border: `1.5px solid ${motivo === k ? 'var(--ch-red)' : 'var(--ch-bdr2)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {motivo === k && <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--ch-red)' }} />}
                </Box>
                {r}
              </Box>
            ))}
          </Box>
          {motivo === 'outro' && (
            <Box sx={{ p: '6px 14px 0' }}>
              <Box component="input" autoFocus aria-label="Qual o motivo?" placeholder="Ex.: quer só atacado"
                maxLength={MOTIVO_TEXTO_MAX} value={texto} onChange={(e) => setTexto(e.target.value)} sx={campo} />
            </Box>
          )}
          {erro && <Box role="alert" sx={{ p: '8px 14px 0', fontSize: 13, color: 'var(--ch-red-d)' }}>{erro}</Box>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', p: '12px 14px 14px' }}>
            <Botao onClick={onClose} disabled={salvando}>Cancelar</Botao>
            <Botao type="submit" principal disabled={!motivoOk || salvando}>{salvando ? 'Salvando…' : 'Desqualificar'}</Botao>
          </Box>
        </Box>
      )}

      {passo === 'venda' && (
        <Box component="form" onSubmit={(e) => {
          e.preventDefault();
          if (valorLido == null) { setErro('Informe o valor da venda, por exemplo 1.250,00.'); return; }
          enviar({ status: 'venda', valor: valorLido });
        }}>
          <Topo onVoltar={voltar}>Venda fechada</Topo>
          <Box sx={{ p: '4px 14px 0' }}>
            <Box component="label" htmlFor="ql-valor" sx={{ display: 'block', fontSize: 13, color: 'var(--ch-text2)', mb: '6px' }}>
              Valor da venda
            </Box>
            <Box sx={{ position: 'relative' }}>
              <Box component="span" aria-hidden sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14.5, color: 'var(--ch-text3)' }}>R$</Box>
              <Box component="input" id="ql-valor" ref={primeiro} inputMode="decimal" autoComplete="off" placeholder="0,00"
                value={valor} onChange={(e) => { setValor(e.target.value); setErro(''); }}
                sx={{ ...campo, pl: '38px', fontVariantNumeric: 'tabular-nums' }} />
            </Box>
            <Box sx={{ minHeight: 20, pt: '6px', fontSize: 12.5, color: 'var(--ch-text3)', fontVariantNumeric: 'tabular-nums' }}>
              {valorLido != null ? formatarValor(valorLido) : 'Use vírgula para os centavos.'}
            </Box>
          </Box>
          {erro && <Box role="alert" sx={{ p: '2px 14px 0', fontSize: 13, color: 'var(--ch-red-d)' }}>{erro}</Box>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', p: '10px 14px 14px' }}>
            <Botao onClick={onClose} disabled={salvando}>Cancelar</Botao>
            <Botao type="submit" principal disabled={!valor.trim() || salvando}>{salvando ? 'Salvando…' : 'Marcar venda'}</Botao>
          </Box>
        </Box>
      )}

      {passo === 'inicio' && erro && (
        <Box role="alert" sx={{ p: '0 14px 12px', fontSize: 13, color: 'var(--ch-red-d)' }}>{erro}</Box>
      )}
    </Popover>
  );
}
